/**
 * earnRateHistory — Story 1.4 (Network Earn-Rate-Monitor)
 *
 * Reine Datenlogik fuer den Rate-Verlaufs-Chart.
 *
 * WICHTIG (Contract-Luecke): Der OpenAPI-Contract bietet KEINEN
 * netzwerkweiten Nova-Payer-Rate-Endpunkt und auch keine historische
 * $/GB-Zeitreihe. Der Chart rendert deshalb den ACCOUNT-Verlauf als
 * naechstliegende echte Datenbasis:
 *
 *   Rate(Tag) = (HNT-Ertrag des Tages x aktueller HNT-Kurs) / GB des Tages
 *
 *  - HNT-Ertrag/Tag:  GET /api/v2/dashboard/daily_income (Feld `hnt`)
 *  - GB/Tag:          GET /api/v2/mobile/analytics/summary (`series[].gb`)
 *  - HNT-Kurs:        `hnt_price_usd` aus demselben Summary-Response —
 *                     der Contract liefert keine historischen Tageskurse,
 *                     die Umrechnung nutzt daher den AKTUELLEN Kurs
 *                     (im UI als Hinweis ausgewiesen, nichts wird erfunden).
 *
 * Tage ohne belastbares Volumen (< MIN_GB_FOR_RATE) oder ohne Ertragswert
 * bekommen `rateUsd: null` — der Chart zeigt dort eine Luecke statt eines
 * erfundenen Werts.
 *
 * REVIEW-FIX F1 (irrefuehrende Preisdarstellung):
 * `daily_income.hnt` ist der LEGACY-PoC-Ertragstopf. Seit HIP-149 ist dieser
 * Topf fuer reine Mobile-Accounts 0 — der Quotient (0 x Kurs) / GB ergab dann
 * eine glatte 0-$/GB-Linie direkt unter einem Hero, der die echte
 * Mobile-Rate (avg_usd_per_gb) zeigt. Zwei verschiedene Groessen, eine
 * Kurve: das las sich wie ein Preisverfall auf null und ist als
 * Steuer-/Preisaussage schlicht falsch.
 * Deshalb gilt jetzt: `hnt <= 0` ist KEIN Datenpunkt, sondern eine Luecke.
 * Reicht die Abdeckung nicht (siehe `hasReliableRateHistory`), rendert die
 * Seite gar keine Kurve, sondern einen Empty-State, der die Datenluecke
 * benennt.
 *
 * BE-FOLLOW-UP: Was der Chart eigentlich braucht, ist eine echte
 * $/GB-Zeitreihe (Netzwerk-Nova-Payer-Rate bzw. mindestens
 * mobile/analytics-Rewards je Bucket + historische HNT-Tageskurse). Solange
 * der Contract die nicht liefert, bleibt hier nur der Legacy-Proxy inkl.
 * seiner Luecken — nichts wird interpoliert oder hochgerechnet.
 */

/** HIP-143: Basisrate 1,0x in $/GB. */
export const NOVA_BASE_RATE_USD = 0.1
/** HIP-143: Floor = 0,5x der Basisrate. */
export const NOVA_FLOOR_MULTIPLIER = 0.5
/** HIP-143: Cap = 3,0x der Basisrate. */
export const NOVA_CAP_MULTIPLIER = 3
export const NOVA_FLOOR_USD = NOVA_BASE_RATE_USD * NOVA_FLOOR_MULTIPLIER
export const NOVA_CAP_USD = NOVA_BASE_RATE_USD * NOVA_CAP_MULTIPLIER
/** Design-Prototyp earn-rate.html: Floor-Band unter 0,08 $, Cap-Band ueber 0,24 $. */
export const FLOOR_BAND_USD = 0.08
export const CAP_BAND_USD = 0.24

/** Unter dieser Tagesmenge ist ein $/GB-Quotient reines Rauschen. */
export const MIN_GB_FOR_RATE = 0.01

export type SummarySeriesBucket = {
  bucket: string
  gb: number
}

export type DailyIncomeEntry = {
  date?: string
  hnt?: number
}

export type RateHistoryPoint = {
  /** ISO-Datum (YYYY-MM-DD) des Buckets. */
  date: string
  gb: number
  hntRewards: number | null
  /** $/GB des Tages oder null (Luecke — kein belastbarer Wert). */
  rateUsd: number | null
}

/**
 * Join der GB-Zeitreihe mit den taeglichen HNT-Ertraegen ueber das Datum.
 * Reihenfolge: chronologisch aufsteigend.
 */
export function buildRateHistory(
  series: SummarySeriesBucket[],
  income: DailyIncomeEntry[],
  hntPriceUsd: number | null | undefined,
): RateHistoryPoint[] {
  const hntByDate = new Map<string, number>()
  for (const entry of income) {
    if (entry.date && typeof entry.hnt === 'number') {
      hntByDate.set(entry.date.slice(0, 10), entry.hnt)
    }
  }

  const price = typeof hntPriceUsd === 'number' && hntPriceUsd > 0 ? hntPriceUsd : null

  return [...series]
    .sort((a, b) => a.bucket.localeCompare(b.bucket))
    .map((bucket) => {
      const date = bucket.bucket.slice(0, 10)
      const gb = Number.isFinite(bucket.gb) && bucket.gb > 0 ? bucket.gb : 0
      const hnt = hntByDate.get(date) ?? null
      // F1: hnt <= 0 ist kein „0 $/GB"-Tag, sondern ein Tag ohne Legacy-Ertrag
      // (HIP-149) — also eine Luecke.
      const rateUsd =
        price != null && hnt != null && hnt > 0 && gb >= MIN_GB_FOR_RATE
          ? (hnt * price) / gb
          : null
      return { date, gb, hntRewards: hnt, rateUsd }
    })
}

/**
 * Abdeckung der Verlaufsserie: wie viele Tage einen belastbaren Wert haben.
 */
export function rateHistoryCoverage(points: RateHistoryPoint[]): {
  total: number
  withRate: number
  gaps: number
} {
  const total = points.length
  const withRate = points.reduce((acc, p) => acc + (p.rateUsd != null ? 1 : 0), 0)
  return { total, withRate, gaps: total - withRate }
}

/**
 * F1(b): Eine Kurve wird nur gezeichnet, wenn die Serie ueberhaupt traegt —
 * mindestens ein belastbarer Wert UND hoechstens die Haelfte der Tage als
 * Luecke. Andernfalls zeigt die Seite einen Empty-State mit ehrlichem
 * Hinweis auf die fehlende Datenquelle statt einer Linie, die aus zwei, drei
 * Restwerten einen „Preistrend" konstruiert.
 */
export function hasReliableRateHistory(points: RateHistoryPoint[]): boolean {
  const { total, withRate, gaps } = rateHistoryCoverage(points)
  if (total === 0 || withRate === 0) return false
  return gaps * 2 <= total
}

/**
 * Account-Ø-Referenzlinie fuer den Chart (ACCEPTANCE: "account-Ø line
 * distinct"). Kein zweiter erfundener Datenpunkt — nur der Mittelwert der
 * bereits geplotteten, belastbaren Tageswerte ueber den gewaehlten Zeitraum.
 */
export function accountAverageRate(points: RateHistoryPoint[]): number | null {
  const rates = points.map((p) => p.rateUsd).filter((r): r is number => r != null)
  if (rates.length === 0) return null
  return rates.reduce((sum, r) => sum + r, 0) / rates.length
}

/* ------------------------------------------------------------------ *
 * Chart-Skala (REVIEW-FIX F2)
 *
 * Die alte Skala war `Math.max(0.32, ...rates.map(r => r * 1.08))` plus
 * Gridlines in festen 0,05er-Schritten. Ein einziger Mini-Volumen-Tag
 * (0,02 GB -> 150 $/GB) hat damit die Domain auf 162 $ gerissen und ueber
 * 3000 Gridline-Gruppen (= zehntausende SVG-Knoten) erzeugt, waehrend die
 * eigentliche Kurve als flache Linie am unteren Rand klebte.
 *
 * Jetzt: robuste Domain (P95 bzw. hartes Cap-Band-Vielfaches) und eine
 * feste Anzahl Gridlines mit berechneter Schrittweite.
 * ------------------------------------------------------------------ */

/** Prototyp-Rahmen der Y-Achse (wird nur nach oben/unten erweitert). */
export const RATE_CHART_BASE_MIN_USD = 0.03
export const RATE_CHART_BASE_MAX_USD = 0.32
/** Zielanzahl Gridlines; hart begrenzt, damit das SVG klein bleibt. */
export const RATE_CHART_TARGET_GRIDLINES = 6
export const RATE_CHART_MAX_GRIDLINES = 12

const NICE_STEPS = [
  0.001, 0.002, 0.0025, 0.005, 0.01, 0.02, 0.025, 0.05, 0.1, 0.2, 0.25, 0.5, 1, 2, 5, 10,
]

/** Quantil einer aufsteigend sortierten Liste (lineare Interpolation). */
export function quantileSorted(sortedAsc: number[], q: number): number | null {
  if (sortedAsc.length === 0) return null
  if (sortedAsc.length === 1) return sortedAsc[0]
  const pos = (sortedAsc.length - 1) * Math.min(Math.max(q, 0), 1)
  const lower = Math.floor(pos)
  const upper = Math.ceil(pos)
  if (lower === upper) return sortedAsc[lower]
  return sortedAsc[lower] + (sortedAsc[upper] - sortedAsc[lower]) * (pos - lower)
}

/**
 * Y-Domain des Rate-Charts. Ausreisser oberhalb von `max` werden vom Chart
 * geklemmt und mit Marker gekennzeichnet (nicht weggelassen).
 */
export function rateChartDomain(rates: number[]): { min: number; max: number } {
  const finite = rates.filter((r) => Number.isFinite(r))
  if (finite.length === 0) {
    return { min: RATE_CHART_BASE_MIN_USD, max: RATE_CHART_BASE_MAX_USD }
  }
  const sorted = [...finite].sort((a, b) => a - b)
  const p95 = quantileSorted(sorted, 0.95) ?? sorted[sorted.length - 1]
  const p05 = quantileSorted(sorted, 0.05) ?? sorted[0]
  // Hartes Band: nie mehr als das 1,5-Fache des Cap-Bands bzw. das P95,
  // je nachdem was groesser ist.
  const ceiling = Math.max(CAP_BAND_USD * 1.5, p95) * 1.08
  const max = Math.max(
    RATE_CHART_BASE_MAX_USD,
    Math.min(sorted[sorted.length - 1] * 1.08, ceiling),
  )
  const min = Math.max(0, Math.min(RATE_CHART_BASE_MIN_USD, p05 * 0.9))
  return { min, max: max > min ? max : min + RATE_CHART_BASE_MAX_USD }
}

/**
 * Gridline-Werte mit berechneter Schrittweite: ~6 Linien, nie mehr als
 * RATE_CHART_MAX_GRIDLINES.
 */
export function rateChartGridValues(min: number, max: number): number[] {
  const span = max - min
  if (!(span > 0)) return [min]
  const raw = span / RATE_CHART_TARGET_GRIDLINES
  let step = NICE_STEPS.find((s) => s >= raw) ?? Math.pow(10, Math.ceil(Math.log10(raw)))
  const values: number[] = []
  for (let guard = 0; guard < 40; guard += 1) {
    values.length = 0
    for (let v = Math.ceil(min / step) * step; v <= max + step * 1e-6; v += step) {
      values.push(Math.round(v / step) * step)
    }
    if (values.length <= RATE_CHART_MAX_GRIDLINES) break
    step *= 2
  }
  return values
}
